import { RestError } from "../../services/rest-service/RestService";
import { useMutation, useQueryClient } from "react-query";
import { IPageConfigurationDefaultModel, PageConfigurationDataModel3, PageConfigurationTypeEnum } from "../../services/page-configuration-service/Models";
import PageConfigurationService from "../../services/page-configuration-service/PageConfigurationService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { keys } from "./Keys";
import {keys as deviceSummaryKeys} from "../group-summary/devices/Keys";
import { keys as wellSummaryKeys } from "../group-summary/wells/Keys";

import { MutationContextType } from "./UseSavePageConfiguration";

export type UseSavePageConfigurationDefaultParameters = {
    groupID: number,
    pageConfigurationType: PageConfigurationTypeEnum
} & BaseUseMutationParameters<IPageConfigurationDefaultModel, IPageConfigurationDefaultModel>;

export function useSavePageConfigurationDefault({
    groupID,
    pageConfigurationType,
    onError,
    onSuccess
}: UseSavePageConfigurationDefaultParameters) {
    const queryClient = useQueryClient();

    return useMutation<IPageConfigurationDefaultModel, RestError, IPageConfigurationDefaultModel, MutationContextType>(
        async (model) => {
            await PageConfigurationService.setPageConfigurationDefault(
                groupID,
                model
            );

            return Promise.resolve(model);
        },
        {
            onMutate: async (model: IPageConfigurationDefaultModel) => {

                const otherKeys = pageConfigurationType === PageConfigurationTypeEnum.SummaryListing_RTS ?
                    deviceSummaryKeys :
                    wellSummaryKeys;                

                await queryClient.cancelQueries(keys.all);
                await queryClient.cancelQueries(otherKeys.all);


                const previousConfigurations = queryClient.getQueryData<PageConfigurationDataModel3>(
                    otherKeys.configurationList(
                        groupID
                    )
                );

                queryClient.setQueryData<PageConfigurationDataModel3>(
                    otherKeys.configurationList(
                        groupID
                    ),

                    (old?: PageConfigurationDataModel3) => {
                        const existing = old ?? new PageConfigurationDataModel3({})
                        return {
                            ...existing,
                            defaultConfigurationID: model.configurationID
                        };
                    }
                );

                return {
                    previousConfigurations
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _item, context) => {

                const otherKeys = pageConfigurationType === PageConfigurationTypeEnum.SummaryListing_RTS ?
                deviceSummaryKeys :
                wellSummaryKeys;                

                if(error && context?.previousConfigurations) {
                    queryClient.setQueryData<PageConfigurationDataModel3>(
                        otherKeys.configurationList(
                            groupID
                        ),
                        context?.previousConfigurations
                    );
                }

                queryClient.invalidateQueries(otherKeys.configurationLists());
            }
        }
    );
}
